import React, {useId, useState} from 'react';
import {Card} from 'antd';
import {
    AimOutlined,
    BulbOutlined,
    CheckCircleOutlined,
    LeftOutlined,
    RightOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    MinusCircleOutlined,
    QuestionCircleOutlined,
    SearchOutlined,
    StopOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import SystemCriterionIcon, {
    SYSTEM_CRITERION_KIND,
} from '../comments/SystemCriterionIcon';
import {
    buildReferenceEvidenceLabel,
    getEvidenceTargetRef,
} from './referenceReviewEvidence';
import '../comments/AICommentCard.css';
import './ReferenceReviewCard.css';

const STATUS_ICONS = {
    aligned: CheckCircleOutlined,
    partially_aligned: ExclamationCircleOutlined,
    not_aligned: CloseCircleOutlined,
    needs_confirmation: QuestionCircleOutlined,
    not_found: SearchOutlined,
    not_applicable: StopOutlined,
    pending: ClockCircleOutlined,
};

const DETAIL_SECTION_ICONS = {
    expected: AimOutlined,
    contract: FileTextOutlined,
    gap: ExclamationCircleOutlined,
    missing: QuestionCircleOutlined,
    action: ToolOutlined,
    confirmation: CheckCircleOutlined,
    available: FileSearchOutlined,
    searched: SearchOutlined,
    absence: StopOutlined,
    justification: InfoCircleOutlined,
};

export const isReferenceReviewCard = (card) => card?.source_type === 'reference_review';

const getCardCriterionName = (card = {}) => (
    card.criterion_name || card.criterion_title || ''
);

export const filterReferenceReviewCards = (cards = [], selectedCriterion = null) => (
    (cards || [])
        .filter((card) => !selectedCriterion || getCardCriterionName(card) === selectedCriterion)
        .slice()
        .sort((a, b) => {
            const orderA = Number.isFinite(Number(a?.source_order)) ? Number(a.source_order) : Number.MAX_SAFE_INTEGER;
            const orderB = Number.isFinite(Number(b?.source_order)) ? Number(b.source_order) : Number.MAX_SAFE_INTEGER;
            return orderA - orderB
                || String(a?.requirement_id || '').localeCompare(String(b?.requirement_id || ''), undefined, {numeric: true});
        })
);

const EvidenceChipList = ({
    card,
    items,
    sourceKind,
    onEvidenceClick,
    maxVisible = 3,
    disabled = false,
    disabledLabel = '',
}) => {
    const {t} = useTranslation('review');
    const [showAll, setShowAll] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [activeIndexByGroup, setActiveIndexByGroup] = useState({});
    const regionId = useId();
    const safeItems = Array.isArray(items) ? items : [];
    if (!safeItems.length) return null;

    const fallbackDocumentId = sourceKind === 'reference'
        ? card.reference_document_id || card.reference_document_title || 'reference'
        : card.contract_document_id || card.contract_document_title || 'contract';
    const groups = safeItems.reduce((result, evidence) => {
        const documentId = evidence.document_version_id
            || evidence.document_id
            || evidence.file_id
            || evidence.document_title
            || fallbackDocumentId;
        const key = `${sourceKind}:${documentId}`;
        const current = result.find((group) => group.key === key);
        if (current) current.items.push(evidence);
        else result.push({key, items: [evidence]});
        return result;
    }, []);
    const visibleGroups = showAll ? groups : groups.slice(0, maxVisible);
    const hiddenCount = Math.max(0, groups.length - maxVisible);
    const openLabel = sourceKind === 'reference'
        ? t('referenceReview.openReferenceEvidence')
        : t('referenceReview.openContractEvidence');

    return (
        <div className="reference-card__evidence-group" id={regionId}>
            {visibleGroups.map((group) => {
                const firstEvidence = group.items[0];
                const firstLabel = buildReferenceEvidenceLabel({
                    evidence: firstEvidence,
                    sourceKind,
                    referenceDocumentTitle: card.reference_document_title
                        || t('referenceReview.referenceDocument'),
                    requirementId: card.requirement_id,
                    contractDocumentTitle: card.contract_document_title
                        || t('referenceReview.contractDocument'),
                });
                const documentLabel = sourceKind === 'reference'
                    ? card.reference_document_title || t('referenceReview.referenceDocument')
                    : firstEvidence.document_title
                        || card.contract_document_title
                        || t('referenceReview.contractDocument');
                const grouped = group.items.length > 1;
                const isExpanded = expandedGroup === group.key;
                const activeIndex = Math.min(activeIndexByGroup[group.key] || 0, group.items.length - 1);
                const label = grouped
                    ? `${documentLabel} · ${card.requirement_id} · ${t('referenceReview.passageCount', {count: group.items.length})}`
                    : firstLabel;
                const Icon = sourceKind === 'reference' ? FileSearchOutlined : LinkOutlined;
                return (
                    <React.Fragment key={group.key}>
                        <button
                            type="button"
                            className={`reference-card__evidence-chip is-${sourceKind}`}
                            disabled={disabled}
                            onClick={() => {
                                if (disabled) return;
                                if (!grouped) {
                                    onEvidenceClick(firstEvidence);
                                    return;
                                }
                                setExpandedGroup(group.key);
                                setActiveIndexByGroup((current) => ({...current, [group.key]: 0}));
                                onEvidenceClick(firstEvidence, {index: 0, total: group.items.length});
                            }}
                            title={disabledLabel || (grouped ? label : `${openLabel} — ${label}`)}
                            aria-label={disabled
                                ? `${label} — ${disabledLabel}`
                                : grouped ? label : `${openLabel}: ${label}`}
                            aria-expanded={grouped ? isExpanded : undefined}
                        >
                            <Icon aria-hidden="true" />
                            <span>{label}</span>
                            {grouped && <DownOutlined className={isExpanded ? 'is-open' : ''} aria-hidden="true" />}
                        </button>
                        {grouped && isExpanded && (
                            <div className="reference-card__evidence-navigation" aria-label={t('referenceReview.passageNavigation')}>
                                <button
                                    type="button"
                                    disabled={activeIndex === 0}
                                    aria-label={t('referenceReview.previousPassage')}
                                    onClick={() => {
                                        const index = Math.max(0, activeIndex - 1);
                                        setActiveIndexByGroup((current) => ({...current, [group.key]: index}));
                                        onEvidenceClick(group.items[index], {index, total: group.items.length});
                                    }}
                                ><LeftOutlined /></button>
                                <span>{activeIndex + 1}/{group.items.length}</span>
                                <button
                                    type="button"
                                    disabled={activeIndex >= group.items.length - 1}
                                    aria-label={t('referenceReview.nextPassage')}
                                    onClick={() => {
                                        const index = Math.min(group.items.length - 1, activeIndex + 1);
                                        setActiveIndexByGroup((current) => ({...current, [group.key]: index}));
                                        onEvidenceClick(group.items[index], {index, total: group.items.length});
                                    }}
                                ><RightOutlined /></button>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    className="reference-card__evidence-chip reference-card__evidence-more"
                    onClick={() => setShowAll((current) => !current)}
                    aria-expanded={showAll}
                    aria-controls={regionId}
                >
                    {showAll
                        ? t('referenceReview.showFewerEvidence')
                        : `+${hiddenCount}`}
                </button>
            )}
        </div>
    );
};

const DetailSection = ({
    label,
    value,
    kind = 'gap',
    className = '',
    secondaryLabel = '',
    secondaryValue = '',
}) => {
    if (!String(value || '').trim()) return null;
    const SectionIcon = DETAIL_SECTION_ICONS[kind] || InfoCircleOutlined;
    return (
        <section className={`reference-card__detail-section is-${kind} ${className}`.trim()}>
            <h4 className="reference-card__detail-heading">
                <SectionIcon aria-hidden="true" />
                <span>{label}</span>
            </h4>
            <p>{value}</p>
            {String(secondaryValue || '').trim() && (
                <div className="reference-card__detail-secondary">
                    <strong>
                        <BulbOutlined aria-hidden="true" />
                        <span>{secondaryLabel}</span>
                    </strong>
                    <p>{secondaryValue}</p>
                </div>
            )}
        </section>
    );
};

const comparableText = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLocaleLowerCase();

const isDistinctText = (value, ...comparedValues) => {
    const normalized = comparableText(value);
    if (!normalized) return false;
    return comparedValues.every((compared) => {
        const other = comparableText(compared);
        return !other || (normalized !== other && !normalized.includes(other) && !other.includes(normalized));
    });
};

const combineDistinctText = (...values) => values.reduce((result, value) => {
    const text = String(value || '').trim();
    if (!text || !isDistinctText(text, ...result)) return result;
    return [...result, text];
}, []).join('\n');

const buildDetailSections = (card, t) => {
    const status = card?.status || 'pending';
    const expected = card?.expected_position;
    const contract = card?.summary;
    const gap = card?.gap;
    const impact = card?.business_impact;
    const recommendation = card?.recommendation;
    const gapValue = gap || impact;
    const impactValue = gap && isDistinctText(impact, gap, contract) ? impact : '';
    const withImpact = (section) => ({
        ...section,
        secondaryLabel: impactValue ? t('referenceReview.whyImportant') : '',
        secondaryValue: impactValue,
    });

    if (status === 'aligned') {
        return [
            {key: 'expected', kind: 'expected', label: t('referenceReview.expected'), value: expected, className: 'is-expected'},
            {key: 'contract', kind: 'contract', label: t('referenceReview.contractPosition'), value: contract, className: 'is-contract'},
            {
                key: 'confirmation',
                kind: 'confirmation',
                label: t('referenceReview.confirmation'),
                value: combineDistinctText(gap, impact, recommendation)
                    || t('referenceReview.alignedConfirmation'),
            },
        ];
    }
    if (status === 'partially_aligned') {
        return [
            {key: 'expected', kind: 'expected', label: t('referenceReview.expected'), value: expected, className: 'is-expected'},
            {key: 'contract', kind: 'contract', label: t('referenceReview.contractPosition'), value: contract, className: 'is-contract'},
            withImpact({key: 'gap', kind: 'missing', label: t('referenceReview.missingElement'), value: gapValue}),
            {key: 'action', kind: 'action', label: t('referenceReview.recommendation'), value: recommendation},
        ];
    }
    if (status === 'not_aligned') {
        return [
            {key: 'expected', kind: 'expected', label: t('referenceReview.expected'), value: expected, className: 'is-expected'},
            {key: 'contract', kind: 'contract', label: t('referenceReview.contractPosition'), value: contract, className: 'is-contract'},
            withImpact({key: 'gap', kind: 'gap', label: t('referenceReview.gap'), value: gapValue}),
            {key: 'action', kind: 'action', label: t('referenceReview.recommendation'), value: recommendation},
        ];
    }
    if (status === 'needs_confirmation') {
        const missing = combineDistinctText(gap, expected);
        return [
            {key: 'available', kind: 'available', label: t('referenceReview.availableInformation'), value: contract, className: 'is-contract'},
            withImpact({key: 'missing', kind: 'missing', label: t('referenceReview.missingInformation'), value: missing}),
            {key: 'action', kind: 'action', label: t('referenceReview.verificationAction'), value: recommendation},
        ];
    }
    if (status === 'not_found') {
        const absence = combineDistinctText(contract, gap);
        return [
            {key: 'searched', kind: 'searched', label: t('referenceReview.searchedElement'), value: expected, className: 'is-expected'},
            withImpact({key: 'absence', kind: 'absence', label: t('referenceReview.absenceFound'), value: absence}),
            {key: 'action', kind: 'action', label: t('referenceReview.recommendation'), value: recommendation},
        ];
    }
    if (status === 'not_applicable') {
        return [{
            key: 'justification',
            kind: 'justification',
            label: t('referenceReview.justification'),
            value: combineDistinctText(contract, expected, gap, impact, recommendation),
        }];
    }
    return [
        {key: 'expected', kind: 'expected', label: t('referenceReview.expected'), value: expected, className: 'is-expected'},
        {key: 'contract', kind: 'contract', label: t('referenceReview.contractPosition'), value: contract, className: 'is-contract'},
        withImpact({key: 'gap', kind: 'gap', label: t('referenceReview.gap'), value: gapValue}),
        {key: 'action', kind: 'action', label: t('referenceReview.recommendation'), value: recommendation},
    ];
};

const getCollapsedSummary = (card, t) => {
    const status = card?.status || 'pending';
    if (status === 'partially_aligned' || status === 'not_aligned') {
        return card?.gap || card?.business_impact || card?.summary || '';
    }
    if (status === 'needs_confirmation') {
        return card?.gap || card?.expected_position || card?.summary || '';
    }
    if (status === 'not_found') {
        return card?.gap || card?.summary || card?.expected_position || '';
    }
    if (status === 'aligned') {
        return t('referenceReview.alignedConfirmation');
    }
    if (status === 'not_applicable') {
        return card?.summary || card?.gap || card?.expected_position || '';
    }
    return card?.summary || card?.gap || '';
};

const evidenceCount = (card = {}) => (
    (Array.isArray(card.reference_evidence) ? card.reference_evidence.length : 0)
    + (Array.isArray(card.contract_evidence) ? card.contract_evidence.length : 0)
);

const isInteractiveCardTarget = (target) => (
    target instanceof Element
    && Boolean(target.closest('button, a, input, select, textarea, [role="button"]'))
);

export default function ReferenceReviewCard({
    card,
    isSelected = false,
    onContractEvidenceClick,
    onReferenceEvidenceClick,
}) {
    const {t} = useTranslation('review');
    const [expanded, setExpanded] = useState(false);
    const detailId = useId();
    const status = card?.status || 'pending';
    const StatusIcon = STATUS_ICONS[status] || MinusCircleOutlined;
    const references = Array.isArray(card?.reference_evidence) ? card.reference_evidence : [];
    const referenceEvidenceAvailable = Boolean(card?.snapshot_id);
    const contractEvidence = Array.isArray(card?.contract_evidence) ? card.contract_evidence : [];
    const totalEvidence = evidenceCount(card);

    const handleReferenceEvidence = (evidence, navigation) => {
        if (onReferenceEvidenceClick) {
            if (navigation) onReferenceEvidenceClick(evidence, navigation);
            else onReferenceEvidenceClick(evidence);
        }
    };

    const handleContractEvidence = (evidence, navigation) => {
        const targetRef = getEvidenceTargetRef(evidence);
        if (!targetRef) return;
        if (navigation) onContractEvidenceClick?.(targetRef, evidence, navigation);
        else onContractEvidenceClick?.(targetRef, evidence);
    };

    const activateCard = () => {
        setExpanded((current) => !current);
        const firstEvidence = contractEvidence.find((evidence) => getEvidenceTargetRef(evidence));
        if (firstEvidence) handleContractEvidence(firstEvidence);
    };

    const handleCardClick = (event) => {
        if (isInteractiveCardTarget(event.target)) return;
        if (window.getSelection?.().toString()) return;
        activateCard();
    };

    const handleCardKeyDown = (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activateCard();
    };

    const cardActionLabel = expanded
        ? t('referenceReview.hideDetail')
        : t('referenceReview.viewDetail');
    const collapsedSummary = getCollapsedSummary(card, t);
    const detailSections = buildDetailSections(card, t).filter((section) => (
        String(section.value || '').trim()
    ));

    return (
        <Card
            className={`ai-comment-card reference-review-card is-${status} ${isSelected ? 'selected is-active' : ''}`.trim()}
            data-comment-id={card?.id}
            data-source-type="reference_review"
            role="article"
            tabIndex={0}
            aria-label={`${getCardCriterionName(card) || t('referenceReview.title')} — ${card?.requirement_id} ${card?.title} — ${cardActionLabel}`}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            size="small"
        >
            <div className="reference-card__topline">
                <span className="criterion-identity reference-card__criterion">
                    <SystemCriterionIcon
                        kind={SYSTEM_CRITERION_KIND.REFERENCE_REVIEW}
                        criterionName={getCardCriterionName(card) || t('referenceReview.title')}
                    />
                    <span className="criterion-tag" title={getCardCriterionName(card)}>
                        <span className="criterion-tag__label">
                            {getCardCriterionName(card) || t('referenceReview.title')}
                        </span>
                    </span>
                </span>
                <span className={`reference-card__status is-${status}`}>
                    <StatusIcon aria-hidden="true" />
                    <span>{t(`referenceReview.status.${status}`)}</span>
                </span>
            </div>

            <div className="reference-card__identity">
                <span>{card?.requirement_id}</span>
                {card?.importance && <span aria-hidden="true">·</span>}
                {card?.importance && (
                    <span>{t(`referenceReview.importance.${card.importance}`)}</span>
                )}
            </div>

            <h3 className="reference-card__title">{card?.title}</h3>
            {collapsedSummary && !expanded && (
                <p className="reference-card__summary">{collapsedSummary}</p>
            )}

            <div className="reference-card__footer">
                <span className="reference-card__evidence-count">
                    <LinkOutlined aria-hidden="true" />
                    {t('referenceReview.evidenceCount', {count: totalEvidence})}
                </span>
                <button
                    type="button"
                    className="reference-card__detail-toggle"
                    onClick={() => setExpanded((current) => !current)}
                    aria-expanded={expanded}
                    aria-controls={detailId}
                    aria-label={`${expanded ? t('referenceReview.hideDetail') : t('referenceReview.viewDetail')}: ${card?.requirement_id} ${card?.title}`}
                >
                    <span>{expanded ? t('referenceReview.hideDetail') : t('referenceReview.viewDetail')}</span>
                    <DownOutlined className={expanded ? 'is-open' : ''} aria-hidden="true" />
                </button>
            </div>

            {expanded && (
                <div className="reference-card__detail" id={detailId}>
                    <div className="reference-card__detail-grid">
                        {detailSections.map(({key, ...section}) => (
                            <DetailSection key={key} {...section} />
                        ))}
                    </div>

                    {totalEvidence > 0 && (
                        <section className="reference-card__proofs" aria-label={t('referenceReview.evidence')}>
                            <h4>{t('referenceReview.evidence')}</h4>
                            {references.length > 0 && !referenceEvidenceAvailable && (
                                <p className="reference-card__evidence-unavailable">
                                    <ClockCircleOutlined aria-hidden="true" />
                                    <span>{t('referenceReview.referenceEvidencePending')}</span>
                                </p>
                            )}
                            <EvidenceChipList
                                card={card}
                                items={references}
                                sourceKind="reference"
                                onEvidenceClick={handleReferenceEvidence}
                                disabled={!referenceEvidenceAvailable}
                                disabledLabel={t('referenceReview.referenceEvidencePending')}
                            />
                            <EvidenceChipList
                                card={card}
                                items={contractEvidence}
                                sourceKind="contract"
                                onEvidenceClick={handleContractEvidence}
                            />
                        </section>
                    )}

                    {card?.confidence && (
                        <p className="reference-card__confidence">
                            {t('referenceReview.confidence')}: {t(`referenceReview.confidenceValue.${card.confidence}`)}
                        </p>
                    )}
                </div>
            )}
        </Card>
    );
}
